import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import i18next from 'i18next';
import { useAuthStore } from '../store/authStore';
import { showRateLimitError } from '../store/notificationStore';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Instance Axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh « single-flight » : partagé entre tous les appels qui reçoivent un 401
// simultanément (fréquent au chargement de page où plusieurs requêtes partent en
// parallèle). Évite que la rotation du refresh token côté serveur ne révoque le
// token sous les pieds des requêtes concurrentes → déconnexions intempestives.
let refreshPromise: Promise<string> | null = null;

async function performTokenRefresh(): Promise<string> {
  const { refreshToken, setTokens } = useAuthStore.getState();

  // Le refresh token transite par le cookie httpOnly (withCredentials) ; le body
  // n'est qu'un repli pour les sessions existantes qui n'ont pas encore le cookie.
  const response = await axios.post(
    `${API_URL}/auth/refresh`,
    refreshToken ? { refreshToken } : {},
    { withCredentials: true }
  );

  const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;

  setTokens({
    accessToken: newAccessToken,
    refreshToken: newRefreshToken,
    expiresIn: response.data.data.expiresIn,
  });

  return newAccessToken;
}

// Intercepteur pour gérer le rafraîchissement des tokens et les erreurs de rate limiting
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code?: string; retryAfter?: number; message?: string }>) => {
    const originalRequest = error.config;

    // Gérer les erreurs de rate limiting (429)
    if (error.response?.status === 429) {
      const retryAfter = error.response.data?.retryAfter;
      const backendMessage = error.response.data?.message;
      showRateLimitError(retryAfter, backendMessage);
      return Promise.reject(error);
    }

    // Si erreur 401 et qu'on n'a pas encore réessayé
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry
    ) {
      (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry = true;

      const { logout } = useAuthStore.getState();

      try {
        // Un seul refresh en vol partagé par toutes les requêtes concurrentes.
        if (!refreshPromise) {
          refreshPromise = performTokenRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccessToken = await refreshPromise;

        // Réessayer la requête originale
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return api(originalRequest);
      } catch {
        // Session réellement expirée/fermée côté serveur : purge locale + redirection
        // propre (évite l'état « connecté en local mais fermé côté serveur »).
        logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Types de réponse
export interface ApiError {
  success: false;
  message: string;
  errors?: Array<{ field: string; message: string }>;
}

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error);
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error) && error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return i18next.t('errorOccurred', { ns: 'common' });
}
